import { createFeature, createReducer, on } from '@ngrx/store';

import type { ApiStatus } from '@amfam/shared/models/lib/api/api-status.model';

import { PreferencesExpApi } from '../models/user-account-preferences.model';
import {
  loadPreferences,
  loadPreferencesFailure,
  loadPreferencesSuccess,
  updatePreferences,
  updatePreferencesFailure,
  updatePreferencesSuccess
} from './preferences.actions';

export const preferencesFeatureKey = 'preferences';

export interface State {
  preferencesByPolicy: { [policyId: string]: PreferencesExpApi[] };
  loadingPolicies: string[]; // Track which policies are currently loading
  updatingPolicies: string[]; // Track which policies are currently updating
  error: string | Error | null;
  status: ApiStatus | null;
}

const initialState: State = {
  preferencesByPolicy: {},
  loadingPolicies: [],
  updatingPolicies: [],
  error: null,
  status: null
};

export const preferencesReducer = createReducer(
  initialState,

  on(
    loadPreferences,
    (state, { policyNumber }): State => ({
      ...state,
      loadingPolicies: [...state.loadingPolicies, policyNumber]
    })
  ),

  on(loadPreferencesSuccess, (state, { response }): State => {
    const policyId = response.response.policy.policyId;
    return {
      ...state,
      loadingPolicies: state.loadingPolicies.filter(id => id !== policyId),
      preferencesByPolicy: {
        ...state.preferencesByPolicy,
        [policyId]: response.response.policy.preferences // Add/update this policy's preferences
      },
      status: response.status
      // Don't clear error on success - a previous policy may have failed
    };
  }),

  on(
    loadPreferencesFailure,
    (state, { policyNumber, error }): State => ({
      ...state,
      loadingPolicies: state.loadingPolicies.filter(id => id !== policyNumber), // Remove only the failed policy
      error
    })
  ),

  on(
    updatePreferences,
    (state, { policyId, clearError }): State => ({
      ...state,
      updatingPolicies: [...state.updatingPolicies, policyId],
      error: clearError ? null : state.error // Clear error only on first update
    })
  ),

  on(updatePreferencesSuccess, (state, { policyId, preferenceOption }): State => {
    const policyPreferences = state.preferencesByPolicy[policyId] || [];
    return {
      ...state,
      updatingPolicies: state.updatingPolicies.filter(id => id !== policyId),
      preferencesByPolicy: {
        ...state.preferencesByPolicy,
        [policyId]: policyPreferences.map(pref =>
          pref.businessContext === 'POLICY' && pref.preferenceLabel === 'POLICY_DOCUMENT_DELIVERY'
            ? { ...pref, preferenceOption }
            : pref
        )
      }
    };
  }),

  on(updatePreferencesFailure, (state, { policyId, error }): State => {
    // Force a new error object reference to ensure NGRX detects the change
    const newError = error && typeof error === 'object' ? { ...error } : error;
    return {
      ...state,
      updatingPolicies: state.updatingPolicies.filter(id => id !== policyId), // Remove only the failed policy
      error: newError
    };
  })
);

export const preferencesFeature = createFeature({
  name: preferencesFeatureKey,
  reducer: preferencesReducer
});
